/**
 * API Client for AI Meta-Engine
 * Handles all backend communication with JWT token management
 */

import type {
  User,
  SignupRequest,
  LoginRequest,
  AuthResponse,
  Conversation,
  ChatRequest,
  ChatResponse,
  UserSettings,
  UpdateSettingsRequest,
  UsageSummary,
  UsageStats,
  LimitsStatus,
  ApiResponse,
  ModelInfo,
  TranscribeResponse
} from './types'
import {
  LoginResponseSchema,
  MeResponseSchema,
  SettingsResponseSchema,
  UserSchema,
  validateResponse,
} from './apiSchemas'
import { emitAuthExpired } from './authEvents'
import type { TierName } from './tiers'

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

// ============================================================================
// Token Management
// ============================================================================
//
// Storage model (C4 fix):
//   - Access token (15-min JWT) lives in module-scope memory only. Cleared on
//     tab close. Never written to localStorage, sessionStorage, or cookies —
//     keeps it out of reach of any XSS payload that survives long enough to
//     read storage APIs.
//   - Refresh token (30-day rotating) rides in an httpOnly + Secure +
//     SameSite=Strict cookie set by the server (`__Host-arbinex_rt` in prod).
//     The browser sends it automatically on credentialed XHRs; JavaScript
//     cannot read it via `document.cookie`.
//
// All authenticated fetches use `credentials: 'include'` so the cookie
// travels on /api/auth/refresh and /api/auth/revoke.

let _accessToken: string | null = null

export const tokenManager = {
  get(): string | null {
    return _accessToken
  },

  set(token: string): void {
    _accessToken = token
  },

  remove(): void {
    _accessToken = null
  },

  isValid(): boolean {
    const token = _accessToken
    if (!token) return false

    try {
      // Decode JWT to check expiration (basic check)
      const payload = JSON.parse(atob(token.split('.')[1]))
      const exp = payload.exp * 1000 // Convert to milliseconds
      return Date.now() < exp
    } catch {
      return false
    }
  }
}

/** Result of a refresh attempt. `user` is present only when the control-plane
 *  returns it inline on the 200 (perf/fast-reload A) — it is `null`/absent when
 *  refresh fails OR when talking to an older control-plane that returns `{token}`
 *  only, which is the signal for callers to fall back to GET /api/auth/me. */
interface RefreshResult {
  ok: boolean
  user?: User | null
}

// Deduplicates concurrent refresh attempts into a single in-flight request.
// Critical with cookie-rotated refresh tokens: N parallel 401s without
// dedup would issue N refresh calls, N-1 of which would race the rotation
// and trip the family-revoke detector (services/refreshToken.ts), nuking
// the session. BOTH the 401-retry path (attemptTokenRefresh) and boot-time
// rehydration (attemptSilentRefresh) share this promise, so a reload racing
// an in-flight 401 refresh collapses to one call.
let _refreshPromise: Promise<RefreshResult> | null = null

/**
 * Shared single-flight refresh. Exchanges the refresh-token cookie (sent
 * automatically via `credentials: 'include'`) for a new in-memory access token,
 * and surfaces the inline user when the control-plane provides it.
 */
async function refreshSession(): Promise<RefreshResult> {
  if (_refreshPromise) return _refreshPromise

  _refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })

      if (!response.ok) {
        tokenManager.remove()
        return { ok: false }
      }

      const data = await response.json() as {
        success: boolean
        data?: { token: string; user?: User | null }
      }

      if (data.success && data.data?.token) {
        tokenManager.set(data.data.token)
        // Validate the inline user against the same schema /me applies, so
        // backend/frontend user-shape drift still surfaces a dev-time warning on
        // this path — steady-state boot no longer routes through authApi.me().
        // Graceful degradation: validateResponse warns and casts, never throws.
        const user = data.data.user
          ? (validateResponse(UserSchema, data.data.user, 'refresh:user') as User)
          : null
        return { ok: true, user }
      }

      tokenManager.remove()
      return { ok: false }
    } catch {
      tokenManager.remove()
      return { ok: false }
    } finally {
      _refreshPromise = null
    }
  })()

  return _refreshPromise
}

/**
 * Attempt to get a new access token using the refresh-token cookie.
 * The cookie travels automatically via `credentials: 'include'`.
 *
 * Returns true if the refresh succeeded and a new access token is in memory.
 * Signature intentionally kept `Promise<boolean>` so the 401-retry callers stay
 * untouched — the inline user (if any) is only consumed by attemptSilentRefresh.
 */
async function attemptTokenRefresh(): Promise<boolean> {
  const result = await refreshSession()
  return result.ok
}

/**
 * Public boot-time silent refresh. Called once during app initialization
 * (authStore) to rehydrate the access token from the refresh cookie after a
 * page reload. Resolves `{ ok, user }` — a truthy `user` lets bootstrap set the
 * session in one pass with no follow-up /api/auth/me round-trip.
 */
export async function attemptSilentRefresh(): Promise<RefreshResult> {
  return refreshSession()
}

// ============================================================================
// HTTP Client
// ============================================================================

interface RequestOptions extends RequestInit {
  requireAuth?: boolean
}

/** A non-empty string, or null — anything else in an error body is not copy. */
const nonEmptyString = (v: unknown): string | null =>
  typeof v === 'string' && v ? v : null

/**
 * Extract a human error message from an API error body, tolerating BOTH
 * response-envelope shapes the backend uses during the ENVELOPE-CLEANUP
 * migration:
 *   - Rule-7 (the target shape):    { success:false, data:{ error, message? } }
 *   - legacy top-level (migrating): { success:false, error, message? }
 *
 * Order: data.message → data.error → message → error → status fallback.
 *
 * The two keys are NOT interchangeable, and that is what dictates the order.
 * `error` is a terse code ('Rate limit exceeded'); `message` is the sentence a
 * human reads ('Too many requests. Please wait 30 seconds.'). Within a shape,
 * the sentence always wins. Across shapes, the nested pair wins, because a body
 * carrying both is a newer backend behind a stale cache.
 *
 * Reading `message` before `error` within each shape is what makes the backend
 * migration invisible to users: the same logical error resolves to the same
 * string before and after a route's batch lands. The old order (data.error →
 * message → error) satisfied every shape assertion and still swapped ~50
 * user-visible strings for their terse codes the moment a route reshaped —
 * see `api-error-envelope.test.ts`, which pins the equivalence, and
 * `Plans/Backlog/ENVELOPE-CLEANUP.md` finding C1.
 *
 * Reading nested at all is what stops Rule-7 endpoints' real message from being
 * dropped in favour of the generic "Request failed with status N" (Qodo #468 finding 3).
 */
export function extractApiError(payload: unknown, fallback: string): string {
  const p = payload as
    | { message?: unknown; error?: unknown; data?: { error?: unknown; message?: unknown } | null }
    | null
    | undefined

  return (
    nonEmptyString(p?.data?.message) ??
    nonEmptyString(p?.data?.error) ??
    nonEmptyString(p?.message) ??
    nonEmptyString(p?.error) ??
    fallback
  )
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { requireAuth = false, ...fetchOptions } = options

  // For FormData/Blob/URLSearchParams/ArrayBuffer bodies, the browser must set
  // Content-Type itself so it can include the multipart boundary or correct
  // encoding. Setting application/json on those bodies corrupts the request.
  const body = fetchOptions.body
  const isStructuredBody =
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof URLSearchParams ||
    body instanceof ArrayBuffer

  const buildHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {
      ...(fetchOptions.headers as Record<string, string>)
    }

    if (!isStructuredBody && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json'
    }

    if (requireAuth) {
      const token = tokenManager.get()
      if (!token) {
        throw new Error('Authentication required')
      }
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  const url = `${API_BASE_URL}${endpoint}`

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers: buildHeaders(),
      credentials: 'include',
    })

    // Parse JSON response first to access error details
    const data = await response.json()

    // Handle 401: if authenticated request, attempt token refresh and retry
    if (response.status === 401) {
      if (requireAuth) {
        const refreshed = await attemptTokenRefresh()

        if (refreshed) {
          // Retry original request with new access token
          const retryResponse = await fetch(url, {
            ...fetchOptions,
            headers: buildHeaders(),
            credentials: 'include',
          })
          const retryData = await retryResponse.json()

          if (!retryResponse.ok) {
            throw new Error(extractApiError(retryData, `Request failed with status ${retryResponse.status}`))
          }

          return retryData as T
        }
      }

      // Refresh failed or not applicable — clear tokens and surface error
      tokenManager.remove()
      emitAuthExpired()
      throw new Error(extractApiError(data, 'Please log in again'))
    }

    // Check if response indicates error (for other status codes)
    if (!response.ok) {
      throw new Error(extractApiError(data, `Request failed with status ${response.status}`))
    }

    return data as T
  } catch (error) {
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Network error - please try again')
  }
}

// ============================================================================
// Auth API
// ============================================================================

/**
 * Narrow an auth response's `data` to the SESSION arm.
 *
 * `AuthResponse['data']` is a union — the session on success, the error payload
 * on failure. Reading `.token` without narrowing typechecks under a merged
 * optional-everything type and yields `undefined` at runtime, which is the
 * class of bug the union exists to make impossible.
 */
function authSession(
  response: AuthResponse
): { token: string; user: User } | null {
  const data = response.data
  return data && 'token' in data ? data : null
}

export const authApi = {
  /**
   * Sign up a new user and store both tokens on success.
   */
  async signup(data: SignupRequest): Promise<AuthResponse> {
    const response = await request<AuthResponse>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data)
    })

    const session = authSession(response)
    if (response.success && session) {
      tokenManager.set(session.token)
    }

    return response
  },

  /**
   * Log in existing user
   */
  async login(data: LoginRequest): Promise<AuthResponse> {
    const raw = await request<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(data)
    })

    // Validate critical auth response shape
    const response = validateResponse(LoginResponseSchema, raw, 'login') as AuthResponse

    // Save access token to memory; refresh token arrives via httpOnly cookie
    const session = authSession(response)
    if (response.success && session) {
      tokenManager.set(session.token)
    }

    return response
  },

  /**
   * Get current user info (requires auth)
   */
  async me(): Promise<{ success: boolean; data?: { user: User } | { error: string; message?: string } }> {
    const raw = await request('/api/auth/me', {
      requireAuth: true
    })
    return validateResponse(MeResponseSchema, raw, 'me') as { success: boolean; data?: { user: User }; error?: string }
  },

  /**
   * Update user profile (requires auth)
   */
  async updateProfile(data: {
    name?: string
    email?: string
    timezone?: string
    currentPassword?: string
    newPassword?: string
    tier?: TierName
  }): Promise<{ success: boolean; data?: { user: User } | { error: string; message?: string } }> {
    return request('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
      requireAuth: true
    })
  },

  /**
   * Log out current user.
   *
   * Awaits the server-side revoke so the browser processes Set-Cookie
   * (clearing the refresh cookie) BEFORE we clear local state or navigate.
   * If the navigation races the response the cookie can persist — security
   * review §5.
   */
  async logout(): Promise<void> {
    const token = tokenManager.get()
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/api/auth/revoke`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        })
      } catch {
        // Best-effort — local clear below is the authoritative client action
      }
    }
    tokenManager.remove()
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return tokenManager.isValid()
  },

  /**
   * Request password reset email
   */
  async forgotPassword(data: { email: string }): Promise<{ success: boolean; data?: { message: string } | { error: string; message?: string } }> {
    return request('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  },

  /**
   * Verify reset token is valid
   */
  async verifyResetToken(token: string): Promise<{ success: boolean; data?: { valid: boolean; email?: string } | { error: string; message?: string } }> {
    return request(`/api/auth/verify-reset-token?token=${encodeURIComponent(token)}`)
  },

  /**
   * Reset password with token
   */
  async resetPassword(data: { token: string; newPassword: string }): Promise<{ success: boolean; data?: { message: string } | { error: string; message?: string } }> {
    return request('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  },

  /**
   * Verify email with token from verification email
   */
  async verifyEmail(token: string): Promise<{ success: boolean; data?: { message: string } | { error: string; message?: string } }> {
    return request(`/api/auth/verify-email?token=${encodeURIComponent(token)}`)
  },

  /**
   * Resend verification email
   */
  async resendVerification(data: { email: string }): Promise<{ success: boolean; data?: { message: string } | { error: string; message?: string } }> {
    return request('/api/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  },


  // ============================================================================
  // Google OAuth Social Login (E1.7)
  // ============================================================================

  /**
   * Exchange Google access token for internal JWT
   * Called after successful Google OAuth login (implicit flow)
   * S1.17.3: Includes UTM params for CAC tracking on new signups
   */
  async googleCallback(accessToken: string, utmParams?: { utmSource?: string | null; utmMedium?: string | null; utmCampaign?: string | null }): Promise<{
    success: boolean
    data?: {
      user: User & { profilePicture?: string; authProvider?: string }
      token: string
      isNewUser: boolean
    }
    error?: string
    message?: string
  }> {
    return request('/api/auth/google-callback', {
      method: 'POST',
      body: JSON.stringify({ accessToken, ...utmParams })
    })
  },

  /**
   * Set password for Google OAuth-only users
   * Allows users who signed up via Google to also use email/password
   */
  async linkAccount(password: string): Promise<{
    success: boolean
    message?: string
    error?: string
  }> {
    return request('/api/auth/link-account', {
      method: 'POST',
      body: JSON.stringify({ password }),
      requireAuth: true
    })
  },

  /**
   * Mark the setup wizard as completed
   * Called when user skips or finishes the wizard
   */
  async completeWizard(): Promise<{
    success: boolean
    message?: string
    error?: string
  }> {
    return request('/api/auth/complete-wizard', {
      method: 'POST',
      requireAuth: true
    })
  }
}

// ============================================================================
// Conversations API
// ============================================================================

export const conversationsApi = {
  /**
   * Get all conversations for current user
   */
  /**
   * ENVELOPE-CLEANUP 2b: `pagination` moved from a third top-level key to
   * `data.pagination`, which necessarily moved the LIST from `data` to
   * `data.conversations`. That second half is what the plan's "pagination has
   * zero consumers" measurement did not cover — the key had none, but `data`
   * itself certainly did, and three e2e smoke tests caught it.
   */
  async list(): Promise<
    ApiResponse<{ conversations: Conversation[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }>
  > {
    return request('/api/conversations', {
      requireAuth: true
    })
  },

  /**
   * Create a new conversation
   */
  async create(title: string): Promise<ApiResponse<Conversation>> {
    return request('/api/conversations', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ title })
    })
  },

  /**
   * Get a specific conversation with messages
   */
  async get(id: string): Promise<ApiResponse<Conversation>> {
    return request(`/api/conversations/${id}`, {
      requireAuth: true
    })
  },

  /**
   * Update a conversation's title
   */
  async update(
    id: string,
    title: string
  ): Promise<ApiResponse<Conversation>> {
    return request(`/api/conversations/${id}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify({ title })
    })
  },

  /**
   * Delete a conversation
   */
  async delete(id: string): Promise<ApiResponse<{ success: boolean }>> {
    return request(`/api/conversations/${id}`, {
      method: 'DELETE',
      requireAuth: true
    })
  },

  /**
   * Update specific metadata fields on a message (e.g., selectedResponseModel)
   */
  async updateMessageMetadata(conversationId: string, messageId: string, metadata: { selectedResponseModel?: string }): Promise<ApiResponse<{ updated: boolean }>> {
    return request(`/api/conversations/${conversationId}/messages/${messageId}/metadata`, {
      method: 'PATCH',
      requireAuth: true,
      body: JSON.stringify(metadata)
    })
  }
}

// ============================================================================
// Chat API
// ============================================================================

export const chatApi = {
  /**
   * Send a message and get AI responses
   */
  async sendMessage(data: ChatRequest): Promise<ChatResponse> {
    return request('/api/chat', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data)
    })
  },

  /**
   * Get status for a research/orchestration request (polling-based)
   * Returns the current status from Redis, or null if not found
   */
  async getStatus(requestId: string): Promise<ApiResponse<{
    requestId: string
    currentPhase: string
    phaseHistory: Array<{
      phase: string
      timestamp: number
      provider?: string
      sourceCount?: number
      message?: string
      action?: string
      actionDetail?: string
      mode?: 'research' | 'orchestration'
      queryCount?: { current: number; total: number }
      processingStep?: { current: number; total: number; description: string }
    }>
    version: number
    createdAt: number
    updatedAt: number
  } | null>> {
    return request(`/api/chat/status/${encodeURIComponent(requestId)}`, {
      requireAuth: true
    })
  },

  /**
   * Poll for background synthesis result
   * Returns 200 with synthesized data if complete, 202 if pending, 404 if not found
   */
  async getSynthesis(requestId: string): Promise<ApiResponse<{
    synthesisStatus: 'pending' | 'complete' | 'failed'
    synthesized?: import('./types').SynthesizedResponse
  }>> {
    return request(`/api/chat/synthesis/${encodeURIComponent(requestId)}`, {
      requireAuth: true
    })
  },

  /**
   * Trigger on-demand synthesis for a message that doesn't have one
   */
  async triggerSynthesis(messageId: string): Promise<ApiResponse<{
    synthesisStatus: 'complete' | 'failed'
    synthesized?: import('./types').SynthesizedResponse
  }>> {
    return request('/api/chat/synthesis/on-demand', {
      method: 'POST',
      body: JSON.stringify({ messageId }),
      requireAuth: true
    })
  }
}

// ============================================================================
// Settings API
// ============================================================================

/**
 * Per-provider API key state surfaced in /api/settings GET responses.
 * Backend returns either a masked string (e.g. "sk-...abcd") when a key is
 * set, or null when the user has not configured one.
 */
export interface MaskedApiKeys {
  openai: string | null
  anthropic: string | null
  google: string | null
  groq: string | null
  perplexity: string | null
  xai: string | null
}

/**
 * Settings payload returned by /api/settings GET and /api/settings PUT.
 * Reflects the actual route contract in control-plane/routes/settings.ts.
 */
export interface SettingsPayload {
  apiKeys: MaskedApiKeys
  mode: 'auto' | 'manual'
  monthlyBudget: number
  costPerQueryLimit: number
  evaluationEnabled: boolean
  theme: 'light' | 'dark' | 'system'
}

/**
 * Body accepted by /api/settings PUT. All fields optional — any subset is
 * a partial update. Per-provider API key fields accept null to clear the key.
 */
export interface SettingsUpdatePayload {
  openaiApiKey?: string | null
  anthropicApiKey?: string | null
  googleApiKey?: string | null
  groqApiKey?: string | null
  perplexityApiKey?: string | null
  xaiApiKey?: string | null
  mode?: 'auto' | 'manual'
  evaluationEnabled?: boolean
  theme?: string
  monthlyBudget?: number
  costPerQueryLimit?: number
}

export const settingsApi = {
  /**
   * Get user settings
   */
  async get(): Promise<ApiResponse<UserSettings>> {
    const raw = await request('/api/settings', {
      requireAuth: true
    })
    return validateResponse(SettingsResponseSchema, raw, 'settings') as ApiResponse<UserSettings>
  },

  /**
   * Update user settings
   */
  async update(data: UpdateSettingsRequest): Promise<ApiResponse<UserSettings>> {
    return request('/api/settings', {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data)
    })
  },

  /**
   * Get full settings payload (matches /api/settings GET response shape).
   * Used by the Settings page where the raw masked-keys + preferences shape
   * is needed; `get()` is reserved for the legacy `UserSettings` type.
   */
  async fetchAll(): Promise<ApiResponse<SettingsPayload>> {
    return request('/api/settings', {
      requireAuth: true
    })
  },

  /**
   * Save full settings payload (matches /api/settings PUT contract).
   */
  async saveAll(payload: SettingsUpdatePayload): Promise<ApiResponse<SettingsPayload>> {
    return request('/api/settings', {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(payload)
    })
  }
}

// ============================================================================
// Simple Trial API
// ============================================================================

export interface SimpleTrialStatus {
  isActive: boolean
  /**
   * Whether a trial was ever started for this user.
   *
   * `isActive: false` alone is ambiguous and the banner got it wrong because of
   * it: it covers BOTH "the trial ran out" (10 queries/day applies) and "no
   * trial was ever started" (the 50-queries-a-MONTH cap applies). Those are
   * different limits, and a user who never had a trial was being shown the
   * daily one. Reachable in production — signup treats a failed `startTrial` as
   * non-fatal so that an account is never lost to it.
   */
  hasStarted: boolean
  creditsRemaining: number
  creditsTotal: number
  daysRemaining: number
  dailyQueriesUsed: number
  dailyQueryLimit: number
  /** Credits spent in the current rolling 24h trial window. */
  dailyCreditsUsed: number
  /** Credit ceiling per rolling 24h trial window. */
  dailyCreditsLimit: number
  /** Credits still available in the current window, capped by the trial total. */
  dailyCreditsRemaining: number
  /** ISO timestamp at which the current window rolls; null outside an active trial. */
  dailyResetsAt: string | null
  /** ISO timestamp at which the post-trial daily allowance resets (midnight UTC); null during the trial. */
  dailyQueryResetsAt: string | null
}

export const simpleTrialApi = {
  /**
   * Get Simple Free trial status (credits, days, daily usage)
   */
  async getStatus(): Promise<ApiResponse<SimpleTrialStatus | null>> {
    return request('/api/user/simple-trial', {
      requireAuth: true
    })
  }
}

// ============================================================================
// Usage/Analytics API
// ============================================================================

export const usageApi = {
  /**
   * Get usage summary for dashboard
   */
  async getSummary(): Promise<ApiResponse<UsageSummary>> {
    return request('/api/usage', {
      requireAuth: true
    })
  },

  /**
   * Get detailed usage statistics for a period
   */
  async getStats(period: 'day' | 'week' | 'month' = 'month'): Promise<ApiResponse<UsageStats>> {
    return request(`/api/usage/stats?period=${period}`, {
      requireAuth: true
    })
  },

  /**
   * Get current rate limit status and tier limits
   */
  async getLimits(): Promise<ApiResponse<LimitsStatus>> {
    return request('/api/usage/limits', {
      requireAuth: true
    })
  }
}

// ============================================================================
// Models API
// ============================================================================

export const modelsApi = {
  /**
   * Get all available models with metadata
   * Uses backend's /api/chat/models endpoint
   * Backend checks user's API keys and returns isConfigured flag
   */
  async list(): Promise<ApiResponse<{ models: ModelInfo[] }>> {
    return request('/api/chat/models', {
      requireAuth: true
    })
  }
}

// ============================================================================
// Stripe/Subscription API
// ============================================================================

export interface SubscriptionStatus {
  tier: string
  status: string | null
  endDate: string | null
  hasActiveSubscription: boolean
  cancelAtPeriodEnd: boolean
  isTrial: boolean
  trialEndsAt: string | null
  trialUsed: boolean
  billingInterval: 'monthly' | 'annual' | null
}

export interface CheckoutSession {
  sessionId: string
  url: string
}

export interface PortalSession {
  url: string
}

export const stripeApi = {
  /**
   * Create a Stripe Checkout session for Pro subscription
   */
  async createCheckoutSession(tier?: string, interval?: 'monthly' | 'annual'): Promise<ApiResponse<CheckoutSession>> {
    return request('/api/stripe/checkout', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({
        ...(tier && { tier }),
        ...(interval && { interval }),
      })
    })
  },

  /**
   * Create a Stripe Customer Portal session for managing subscription
   */
  async createPortalSession(): Promise<ApiResponse<PortalSession>> {
    return request('/api/stripe/portal', {
      method: 'POST',
      requireAuth: true
    })
  },

  /**
   * Get current subscription status
   */
  async getSubscriptionStatus(): Promise<ApiResponse<SubscriptionStatus>> {
    return request('/api/stripe/subscription', {
      requireAuth: true
    })
  },

  /**
   * Cancel subscription (at period end)
   */
  async cancelSubscription(): Promise<ApiResponse<{ message: string }>> {
    return request('/api/stripe/cancel', {
      method: 'POST',
      requireAuth: true
    })
  },

  /**
   * Reactivate a subscription that was set to cancel at period end
   */
  async reactivateSubscription(): Promise<ApiResponse<{ message: string }>> {
    return request('/api/stripe/reactivate', {
      method: 'POST',
      requireAuth: true
    })
  },

  /**
   * Switch subscription plan across paths (Simple <-> Power) or within a path
   * Returns checkoutUrl for free-to-paid transitions that need Stripe Checkout
   */
  async switchPlan(targetTier: string, interval?: 'monthly' | 'annual'): Promise<ApiResponse<{ checkoutUrl?: string }>> {
    return request('/api/stripe/switch-plan', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ targetTier, ...(interval && { interval }) })
    })
  }
}

// ============================================================================
// Voice Dictation API
// ============================================================================

export const voiceApi = {
  /**
   * Transcribe audio using OpenAI Whisper API
   * Used as fallback when Web Speech API is not available (Firefox, etc.)
   */
  async transcribe(audioBlob: Blob): Promise<TranscribeResponse> {
    const formData = new FormData()
    formData.append('audio', audioBlob, 'recording.webm')

    return request<TranscribeResponse>('/api/transcribe', {
      method: 'POST',
      body: formData,
      requireAuth: true
    })
  }
}

// ============================================================================
// Contact Form (public - no auth required)
// ============================================================================

export async function submitContactForm(data: {
  name: string
  email: string
  message: string
  category?: string
}): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    // `extractApiError`, not a hand-rolled `.error` read. This is the ONLY
    // fetch in this file that bypasses `request()`, which is exactly why it was
    // the one site the envelope migration broke: `/api/contact` routes its 500
    // through `sendError`, so a top-level `.error` read went undefined and the
    // user lost "Please try emailing contact@arbinex.ai directly" — an
    // actionable address — in favour of "Request failed with status 500".
    throw new Error(
      extractApiError(errorData, `Request failed with status ${response.status}`)
    )
  }
}

// ============================================================================
// Feedback API (E1.5.1 - Data Flywheel)
// ============================================================================

export interface FeedbackRequest {
  traceId: string
  messageId: string
  score: 'up' | 'down' | 'copy' | 'implicit_positive' | 'implicit_negative'
  feedbackType: 'selection' | 'synthesis'
  displayedModel?: string
  signalSource?: string
  comment?: string
}

/**
 * AI Concern report payload (ISO 42001 A.2.5).
 * Mirrors the schema enforced server-side in routes/feedback.ts.
 */
export interface AIConcernReport {
  type: 'ai_concern'
  category: 'bias' | 'privacy' | 'safety' | 'transparency' | 'security' | 'quality'
  severity?: 'low' | 'medium' | 'high'
  comment: string
  traceId?: string
  messageId?: string
}

export const feedbackApi = {
  /**
   * Submit explicit feedback (thumbs up/down) or copy signal
   */
  async submit(data: FeedbackRequest): Promise<ApiResponse<{ success: boolean }>> {
    return request('/api/feedback', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data)
    })
  },

  /**
   * Submit implicit feedback signal (fire-and-forget)
   * Does not throw on failure — feedback should never block the user
   */
  submitImplicit(data: FeedbackRequest): void {
    request('/api/feedback', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data)
    }).catch((error) => {
      // Implicit feedback is best-effort; log at debug level for diagnostics
      if (import.meta.env.DEV) {
        console.debug('[feedbackApi] Implicit feedback failed:', error instanceof Error ? error.message : error)
      }
    })
  },

  /**
   * Submit an AI concern report (ISO 42001 A.2.5).
   */
  async aiConcern(payload: AIConcernReport): Promise<ApiResponse<{ success: boolean }>> {
    return request('/api/feedback/ai-concern', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(payload)
    })
  }
}

// ============================================================================
// DORA Metrics API (Admin)
// ============================================================================

export interface DoraMetric {
  value: number
  unit: string
  trend: number
  level: 'Elite' | 'High' | 'Medium' | 'Low'
}

export interface DoraDeployment {
  id: string
  service: string
  environment: string
  status: string
  commitSha: string | null
  version: string | null
  commitMessage: string | null
  startedAt: string
  completedAt: string | null
  leadTimeHours: number | null
}

export interface DoraMetrics {
  period: { days: number; environment: string; since: string }
  metrics: {
    deploymentFrequency: DoraMetric
    leadTime: DoraMetric
    changeFailureRate: DoraMetric
    mttr: DoraMetric
  }
  overall: 'Elite' | 'High' | 'Medium' | 'Low'
  totalDeploys: number
  recentDeploys: DoraDeployment[]
  timestamp: string
}

// ============================================================================
// Admin User Metrics & Waitlist API (§4)
// ============================================================================

export interface TierUserCount {
  tier: string
  users: number
}

export interface UserMetrics {
  total: number
  tiers: TierUserCount[]
  free: {
    admitted: number
    /**
     * Rows still carrying a waitlist stamp. Read it together with `enforced`:
     * with no cap set, nobody is actually being refused, because the predicate
     * releases the whole queue when `MAX_FREE_USERS` is unset.
     */
    waitlisted: number
    /** False when `MAX_FREE_USERS` is unset — the queue exists but holds nobody back. */
    enforced: boolean
    /** `null` means MAX_FREE_USERS is unset, i.e. no cap at all. */
    cap: number | null
    /** `null` whenever `cap` is null — never render it as a number in that case. */
    headroom: number | null
  }
  liveTrials: number
  timestamp: string
}

export interface WaitlistEntry {
  id: string
  email: string
  name: string | null
  tier: string
  waitlistedAt: string | null
  createdAt: string
}

export interface WaitlistPage {
  total: number
  /** How many of `total` this page carries — `total > returned` means truncated. */
  returned: number
  pageSize: number
  users: WaitlistEntry[]
}

export const adminUsersApi = {
  async getMetrics(): Promise<ApiResponse<UserMetrics>> {
    return request('/api/admin/metrics/users', { requireAuth: true })
  },

  async getWaitlist(): Promise<ApiResponse<WaitlistPage>> {
    return request('/api/admin/waitlist', { requireAuth: true })
  },

  async admit(userId: string): Promise<ApiResponse<{ id: string; admitted: boolean }>> {
    return request(`/api/admin/users/${encodeURIComponent(userId)}/admit`, {
      method: 'POST',
      requireAuth: true,
    })
  },
}

export const doraApi = {
  // Environment is not a parameter: the endpoint always reports the environment
  // its own control-plane runs in (each env records deploys only to its own DB).
  async getMetrics(period: '30' | '90' = '30'): Promise<ApiResponse<DoraMetrics>> {
    return request(`/api/admin/dora?period=${period}`, {
      requireAuth: true,
    })
  },
}

// ============================================================================
// Feature Flags API
// ============================================================================

export const flagsApi = {
  /**
   * List runtime feature flags for the current user.
   * Backend returns a `Record<string, boolean>` keyed by flag name.
   */
  async list(): Promise<ApiResponse<Record<string, boolean>>> {
    return request('/api/flags', {
      requireAuth: true
    })
  }
}

// ============================================================================
// User API (account-level: GDPR export, account deletion, feature config)
// ============================================================================

export interface FeatureInfo {
  key: string
  type: 'boolean' | 'limit'
  enabled: boolean
  limitValue?: number
  minimumTier: string | null
}

export interface RateLimitConfig {
  requests: number
  windowMinutes: number
}

export interface UserFeaturesResponse {
  enforcementEnabled: boolean
  currentTier: string
  features: FeatureInfo[]
  rateLimits: { chat: RateLimitConfig; auth: RateLimitConfig }
  /**
   * §4 free-user cap: signed up while the free tier was full, not yet admitted.
   *
   * Optional on the wire because it is a property of the user row rather than
   * of the tier, so it is attached by the route, not by the feature registry.
   * Treat an absent value as `false` — an older control-plane that does not
   * send it must not lock everybody out of the composer.
   */
  waitlisted?: boolean
}

export const userApi = {
  async getFeatures(): Promise<ApiResponse<UserFeaturesResponse>> {
    return request('/api/user/features', { requireAuth: true })
  },

  /**
   * GDPR data export. Backend returns the full account dump as JSON;
   * the caller is expected to serialize it back to a Blob for download.
   */
  async exportData(): Promise<unknown> {
    return request<unknown>('/api/user/export', { requireAuth: true })
  },

  async deleteAccount(): Promise<ApiResponse<{ message?: string }>> {
    return request('/api/user', { method: 'DELETE', requireAuth: true })
  }
}

// ============================================================================
// Prompt Testing API (Pro tier — multi-model prompt comparison)
// ============================================================================

export interface PromptTestResult {
  model: string
  success: boolean
  content: string
  tokensInput: number
  tokensOutput: number
  cost: number
  latency: number
  cached: boolean
  error?: string
}

export interface PromptTestRunRequest {
  promptText: string
  variables: Record<string, string>
  models: string[]
}

export interface PromptTestRunResponse {
  success: boolean
  data: { results: PromptTestResult[] }
}

export interface PromptTemplateSavePayload {
  name: string
  promptText: string
  variables: string[]
}

export const promptTestingApi = {
  /**
   * Run a prompt across multiple models and return per-model results.
   */
  async run(payload: PromptTestRunRequest): Promise<PromptTestRunResponse> {
    return request<PromptTestRunResponse>('/api/prompt-testing/run', {
      method: 'POST',
      body: JSON.stringify(payload),
      requireAuth: true
    })
  },

  /**
   * Save a prompt template for reuse.
   */
  async saveTemplate(payload: PromptTemplateSavePayload): Promise<ApiResponse<{ id: string }>> {
    return request('/api/prompt-templates', {
      method: 'POST',
      body: JSON.stringify(payload),
      requireAuth: true
    })
  }
}

// ============================================================================
// Export default API client
// ============================================================================

const api = {
  auth: authApi,
  conversations: conversationsApi,
  chat: chatApi,
  settings: settingsApi,
  usage: usageApi,
  models: modelsApi,
  stripe: stripeApi,
  voice: voiceApi,
  feedback: feedbackApi,
  dora: doraApi,
  adminUsers: adminUsersApi,
  flags: flagsApi,
  user: userApi,
  promptTesting: promptTestingApi
}

export default api
